// The sheet, in whichever mode the flow is in.
//
// Controlled: it holds no state of its own beyond which contact fields are
// open, and reports every gesture as an event. That keeps the sheet honest —
// what carries between steps is decided in entryFlow, where it can be tested
// without a DOM, and this file only decides what a mode looks like.

import { useEffect, useState } from 'react'

import { formatMonthPeriod } from 'src/utils/date/formatMonthPeriod'

import type {
  CarrierChoice,
  ContactChoice,
  FlowEvent,
  FlowState,
} from '../lib/entryFlow'
import { isGapLikeQuery } from '../lib/searchQuery'
import type { EntryDraft } from '../lib/validation'
import { attentionLabel } from '../lib/validation'

import { ComplianceSection } from './ComplianceSection'
import { ConfirmSheet } from './ConfirmSheet'
import { ContactStep } from './ContactStep'
import { DetailsSection } from './DetailsSection'
import { EmployerCard } from './EmployerCard'
import { EquipmentSection } from './EquipmentSection'
import { QuestionStep } from './QuestionStep'
import { SearchStep } from './SearchStep'
import { SheetShell } from './SheetShell'
import { UnemploymentForm } from './UnemploymentForm'

export interface EntryModalProps {
  state: FlowState
  onEvent: (event: FlowEvent) => void
  onRemove?: () => void
  results?: CarrierChoice[]
  searching?: boolean
  searchFailed?: boolean
  /** True once a query has run, so an empty list means "none" not "not yet". */
  searched?: boolean
  knownContacts?: ContactChoice[]
  /** The lookup that finds known contacts is still out. */
  contactsLoading?: boolean
  /**
   * The employer answers verifications only through a platform, so there is
   * nobody for the driver to name. Drivers are never told why: which carriers
   * route where is ours to know, not theirs.
   */
  contactNotNeeded?: boolean
  /** The previous entry, offered as a source for the two portable answers. */
  copyFrom?: EntryDraft | null
  maxMonth?: number
}

const hasHeldContact = (contact: ContactChoice | null): boolean =>
  Boolean(contact && (contact.name || contact.phone || contact.email))

const primary =
  'w-full rounded-lg bg-doubleNickel-brand-500 p-3 text-center text-sm font-semibold text-white hover:bg-doubleNickel-brand-700'
const tonal =
  'w-full rounded-lg border border-doubleNickel-brand-500 bg-doubleNickel-brand-25 p-3 text-center text-sm font-semibold text-doubleNickel-brand-700'
const quiet =
  'w-full p-2 text-center text-sm font-semibold text-doubleNickel-gray-600 underline underline-offset-2'

/** Below this the search does not run, so there is nothing to have tried. */
const MIN_SEARCH = 2

export const EntryModal = ({
  state,
  onEvent,
  onRemove,
  results = [],
  searching = false,
  searchFailed = false,
  searched = false,
  knownContacts = [],
  contactsLoading = false,
  contactNotNeeded = false,
  copyFrom,
  maxMonth,
}: EntryModalProps) => {
  // Reset whenever the sheet leaves the contact step. It only ever flipped true,
  // so a driver who chose to type a contact once never saw the contacts we hold
  // again — on the next employer, or the next time the sheet opened.
  const [entering, setEntering] = useState(false)
  useEffect(() => {
    if (state.mode !== 'contact') setEntering(false)
  }, [state.mode])
  const { mode, draft, errors, touched } = state
  if (mode === 'closed') return null

  const shown = { ...touched, ...errors }
  const outstanding = Object.keys(errors).length
  const editing = state.editingId !== null

  if (mode === 'question') {
    return (
      <SheetShell
        title="Were you working during this time?"
        height="half"
        subtitle={state.period ? formatMonthPeriod(state.period) : undefined}
      >
        <QuestionStep
          onAnswer={(employed) => onEvent({ type: 'answerEmployed', employed })}
        />
      </SheetShell>
    )
  }

  if (mode === 'confirmRemove') {
    return (
      <SheetShell>
        <ConfirmSheet
          title={
            draft.kind === 'unemployment'
              ? 'Remove this unemployment period?'
              : `Remove ${draft.companyName}?`
          }
          detail={
            draft.startMonth != null && draft.endMonth != null
              ? `${formatMonthPeriod({
                  start: draft.startMonth,
                  end: draft.endMonth,
                })} will become unaccounted for, and you will need to add something in its place.`
              : undefined
          }
          confirmLabel="Remove"
          onConfirm={() => onRemove?.()}
          onCancel={() => onEvent({ type: 'cancelRemove' })}
        />
      </SheetShell>
    )
  }

  if (mode === 'unemployment') {
    return (
      <SheetShell
        title="Time between jobs"
        height="tall"
        subtitle={state.period ? formatMonthPeriod(state.period) : undefined}
        onDismiss={() => onEvent({ type: 'dismiss' })}
        footer={
          <>
            {outstanding > 0 && (
              <div className="mb-1.5 text-[11px] font-semibold text-doubleNickel-error-700">
                {attentionLabel(outstanding)}
              </div>
            )}
            <button
              type="button"
              className={primary}
              onClick={() => onEvent({ type: 'save' })}
            >
              {editing ? 'Save changes' : 'Save'}
            </button>
          </>
        }
      >
        <UnemploymentForm
          startMonth={draft.startMonth ?? null}
          endMonth={draft.endMonth ?? null}
          reason={draft.unemploymentReason ?? null}
          maxMonth={maxMonth}
          errors={shown}
          onChange={(patch) => onEvent({ type: 'patch', patch })}
        />
        {editing && onRemove && (
          <button
            type="button"
            onClick={() => onEvent({ type: 'requestRemove' })}
            className="rounded-lg border border-doubleNickel-error-200 bg-doubleNickel-error-50 p-2.5 text-center text-sm font-semibold text-doubleNickel-error-700"
          >
            Remove this period
          </button>
        )}
      </SheetShell>
    )
  }

  if (mode === 'employer') {
    return (
      <SheetShell
        title="Enter employer"
        height="tall"
        onDismiss={() => onEvent({ type: 'dismiss' })}
        footer={
          <>
            {outstanding > 0 && (
              <div className="mb-1.5 text-[11px] font-semibold text-doubleNickel-error-700">
                {attentionLabel(outstanding)}
              </div>
            )}
            <button
              type="button"
              className={primary}
              onClick={() => onEvent({ type: 'continueFromEmployer' })}
            >
              Continue
            </button>
          </>
        }
      >
        <EmployerCard
          name={draft.companyName ?? ''}
          editable
          city={draft.city}
          state={draft.state}
          dotNumber={draft.dotNumber}
          errors={shown}
          onFieldChange={(patch) => onEvent({ type: 'patch', patch })}
          onChange={() => onEvent({ type: 'searchEmployer' })}
        />
      </SheetShell>
    )
  }

  if (mode === 'search') {
    const typed = state.query.trim()
    // The ways out of the registry appear only once a search has answered —
    // with results, with none, or with the registry down. Offering them before
    // that is how 39% of typed employers came from drivers who never searched.
    const settled = typed.length >= MIN_SEARCH && searched && !searching
    return (
      <SheetShell
        title={editing ? 'Change employer' : 'Add Employment'}
        height="full"
        onDismiss={() => onEvent({ type: 'dismiss' })}
        footer={
          settled ? (
            <>
              <button
                type="button"
                className={tonal}
                onClick={() => onEvent({ type: 'enterManually' })}
              >
                {`Enter “${typed}” manually`}
              </button>
              <button
                type="button"
                className={quiet}
                onClick={() => onEvent({ type: 'enterNonDriving' })}
              >
                This wasn’t a driving / DOT-regulated job
              </button>
            </>
          ) : undefined
        }
      >
        <SearchStep
          query={state.query}
          results={results}
          searching={searching}
          searched={searched}
          failed={searchFailed}
          onQuery={(query) => onEvent({ type: 'query', query })}
          onSelect={(carrier) => onEvent({ type: 'selectCarrier', carrier })}
          gapLike={isGapLikeQuery(typed)}
          onSwitchToUnemployment={() =>
            onEvent({ type: 'switchToUnemployment' })
          }
        />
      </SheetShell>
    )
  }

  if (mode === 'contact') {
    return (
      <SheetShell
        title="Add Employment"
        height="tall"
        onDismiss={() => onEvent({ type: 'dismiss' })}
        footer={
          <button
            type="button"
            className={`${primary} disabled:opacity-60`}
            // Continuing before the lookup answers would record a skip for an
            // employer that may need no contact at all
            disabled={contactsLoading && !state.contactByRequest}
            onClick={() => onEvent({ type: 'continueFromContact' })}
          >
            Continue
          </button>
        }
      >
        <EmployerCard
          name={draft.companyName ?? ''}
          facts={[
            draft.dotNumber ? `DOT ${draft.dotNumber}` : null,
            [draft.city, draft.state].filter(Boolean).join(', ') || null,
          ]}
          onChange={() => onEvent({ type: 'editEmployer' })}
        />
        <ContactStep
          known={knownContacts}
          // Until the lookup answers we do not know whether to ask at all, and
          // fields that appear and then vanish read as something going wrong
          loading={contactsLoading && !state.contactByRequest}
          chosen={state.contact}
          entering={entering || knownContacts.length === 0}
          onEnterDifferent={() => {
            setEntering(true)
            // Shed a contact that came from the list, so the fields start blank
            // rather than pre-filled with somebody else's details. Never shed
            // the driver's own typing: they may be returning to it after a look
            // at the list.
            if (state.contact && state.contact.source !== 'manual') {
              onEvent({ type: 'clearContact' })
            }
          }}
          onFixChosen={() => {
            if (state.contact) {
              onEvent({
                type: 'chooseContact',
                contact: { ...state.contact, source: 'manual' },
              })
              onEvent({ type: 'blur' })
            }
            setEntering(true)
          }}
          onBackToResults={() => setEntering(false)}
          onChoose={(contact) => onEvent({ type: 'chooseContact', contact })}
          manual={{
            name: state.contact?.name,
            phone: draft.phone,
            email: draft.email,
          }}
          onManual={(patch) =>
            onEvent({
              type: 'chooseContact',
              contact: {
                name: patch.name ?? state.contact?.name ?? null,
                phone: patch.phone ?? draft.phone ?? null,
                email: patch.email ?? draft.email ?? null,
                source: 'manual',
              },
            })
          }
          onBlur={() => onEvent({ type: 'blur' })}
          errors={shown}
        />
      </SheetShell>
    )
  }

  // mode === 'form'
  return (
    <SheetShell
      title={editing ? draft.companyName : 'Add Employment'}
      height="full"
      onDismiss={() => onEvent({ type: 'dismiss' })}
      footer={
        <>
          {outstanding > 0 && (
            <div className="mb-1.5 text-[11px] font-semibold text-doubleNickel-error-700">
              {attentionLabel(outstanding)}
            </div>
          )}
          <button
            type="button"
            className={primary}
            onClick={() => onEvent({ type: 'save' })}
          >
            {editing ? 'Save changes' : 'Save Employment'}
          </button>
        </>
      }
    >
      <EmployerCard
        name={draft.companyName ?? ''}
        facts={[
          draft.dotNumber ? `DOT ${draft.dotNumber}` : null,
          [draft.city, draft.state].filter(Boolean).join(', ') || null,
        ]}
        // Closed by the time the form is reached: the employer step is where
        // it is entered, and Edit goes back there. Leaving the fields open here
        // asks for the same company twice.
        errors={shown}
        onChange={() => onEvent({ type: 'editEmployer' })}
        contact={state.contact}
        // No contact row at all for an employer that needs none — "No contact"
        // with an Edit beside it reads as something left undone. One the entry
        // already holds is still shown, and still editable.
        onEditContact={
          contactNotNeeded && !hasHeldContact(state.contact)
            ? undefined
            : () => onEvent({ type: 'editContact' })
        }
      />
      <DetailsSection
        startMonth={draft.startMonth ?? null}
        endMonth={draft.endMonth ?? null}
        isCurrent={Boolean(draft.isCurrent)}
        position={draft.position ?? null}
        reasonForLeaving={draft.reasonForLeaving ?? null}
        maxMonth={maxMonth}
        errors={shown}
        onChange={(patch) => onEvent({ type: 'patch', patch })}
      />
      <EquipmentSection
        value={(draft.equipmentTypes ?? []) as never[]}
        onChange={(equipmentTypes) =>
          onEvent({ type: 'patch', patch: { equipmentTypes } })
        }
      />
      <ComplianceSection
        draft={draft}
        errors={shown}
        copyFrom={copyFrom}
        onCopy={(answers) => onEvent({ type: 'patch', patch: answers })}
        onAnswer={(field, value) =>
          onEvent({ type: 'patch', patch: { [field]: value } })
        }
      />
      {editing && onRemove && (
        <button
          type="button"
          onClick={() => onEvent({ type: 'requestRemove' })}
          className="rounded-lg border border-doubleNickel-error-200 bg-doubleNickel-error-50 p-2.5 text-center text-sm font-semibold text-doubleNickel-error-700"
        >
          Remove this employment
        </button>
      )}
    </SheetShell>
  )
}
